<template>
  <v-navigation-drawer
    v-model="drawer"
    right
    temporary
    fixed
    width="400"
    class="preview-drawer"
  >
    <v-card flat height="100%" class="d-flex flex-column">
      <!-- Header -->
      <v-toolbar color="#0EBF67" dark flat height="60" class="flex-grow-0">
        <v-toolbar-title class="font-weight-bold">{{ $t('Preview') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <div class="flex-grow-1 overflow-y-auto preview-content">
        <v-card-text class="px-4">
        <v-form ref="previewForm">
          <!-- No fields message -->
          <div v-if="!dataFields || dataFields.length === 0" class="text-center grey--text py-8">
            {{ $t('No fields added yet. Add fields to see the preview.') }}
          </div>

          <!-- Dynamic Fields -->
          <template v-for="(field, index) in dataFields">
            <!-- Group Field - Expansion Panel -->
            <v-expansion-panels
              v-if="field.isGroup"
              :key="`group-${field._uid || index}`"
              flat
              accordion
              multiple
              v-model="expandedPanels[index]"
              class="mb-4"
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="px-0 py-0 font-weight-bold text-h6" color="transparent">
                  <div class="d-flex align-center" style="gap: 12px;">
                    <span style="color: #0EBF67">{{ field.groupTitle }}</span>
                    <span v-if="field.mandatory" class="red--text">*</span>
                    <v-tooltip v-if="field.helpText" top color="primary" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon style="color:#0EBF67" v-bind="attrs" v-on="on">
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      <span>{{ field.helpText }}</span>
                    </v-tooltip>
                  </div>
                  <template v-slot:actions>
                    <v-icon color="black">mdi-chevron-up</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-0">
                  <!-- Group Fields -->
                  <div
                    v-for="(subField, subIndex) in field.groupFields"
                    :key="`subfield-${field._uid || index}-${subIndex}`"
                    :class="subIndex === field.groupFields.length - 1 ? 'mb-2' : 'mb-4'"
                  >
                    <label class="font-weight-bold black--text d-block mb-2">
                      {{ subField.fieldTitle }}
                      <v-tooltip v-if="subField.helpText" top color="primary" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon style="color:#0EBF67" v-bind="attrs" v-on="on">
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ subField.helpText }}</span>
                      </v-tooltip>
                      <span v-if="subField.mandatory" class="red--text">*</span>
                    </label>
                    <!-- Radio Button Field -->
                    <v-radio-group
                      v-if="subField.dataType === 'radio_button'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      hide-details
                      dense
                      row
                    >
                      <v-radio
                        v-for="(option, optIdx) in (subField.options || [])"
                        :key="optIdx"
                        :label="option"
                        :value="option"
                      ></v-radio>
                    </v-radio-group>
                    <!-- Checkboxes Field -->
                    <div v-else-if="subField.dataType === 'check_boxes'">
                      <v-checkbox
                        v-for="(option, optIdx) in (subField.options || [])"
                        :key="optIdx"
                        v-model="formValues[getFieldKey(field, index, subIndex)]"
                        :label="option"
                        :value="option"
                        hide-details
                        dense
                        class="mt-0"
                      ></v-checkbox>
                    </div>
                    <!-- Text Field -->
                    <v-text-field
                      v-else-if="subField.dataType === 'text_field'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :placeholder="getPlaceholder(subField.dataType)"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                    <!-- Text Area -->
                    <v-textarea
                      v-else-if="subField.dataType === 'text_area'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :placeholder="getPlaceholder(subField.dataType)"
                      outlined
                      dense
                      hide-details
                      rows="3"
                    ></v-textarea>
                    <!-- Text Field Numeric -->
                    <v-text-field
                      v-else-if="subField.dataType === 'text_field_numeric'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :placeholder="getPlaceholder(subField.dataType)"
                      type="number"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                    <!-- Dropdown / Data List -->
                    <v-select
                      v-else-if="subField.dataType === 'dropdown' || subField.dataType === 'data_list'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :items="subField.options || []"
                      :placeholder="$t('Make a selection')"
                      append-icon="mdi-menu-down"
                      outlined
                      dense
                      hide-details
                    ></v-select>
                    <!-- Date -->
                    <v-text-field
                      v-else-if="subField.dataType === 'date_field'"
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :placeholder="$t('Select date')"
                      type="date"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                     <!-- Attach File -->
                     <v-file-input
                        v-else-if="subField.dataType === 'attach_file'"
                        v-model="formValues[getFieldKey(field, index, subIndex)]"
                        :placeholder="$t('Choose file')"
                        prepend-icon=""
                        append-icon="mdi-paperclip"
                        outlined
                        dense
                        hide-details
                     ></v-file-input>
                     <!-- Measurement Unit -->
                     <v-text-field
                        v-else-if="subField.dataType === 'measurement_unit'"
                        v-model="formValues[getFieldKey(field, index, subIndex)]"
                        :placeholder="$t('Enter value')"
                        :suffix="subField.measurementType || ''"
                        type="number"
                        outlined
                        dense
                        hide-details
                     ></v-text-field>
                     <!-- Digital Signature -->
                     <DigitalSignatureField
                        v-else-if="subField.dataType === 'digital_signature'"
                        :field="signatureFieldStates[getFieldKey(field, index, subIndex)]"
                     />
                    <!-- Default fallback -->
                    <v-text-field
                      v-else
                      v-model="formValues[getFieldKey(field, index, subIndex)]"
                      :placeholder="$t('Enter here')"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Single Field -->
            <div
              v-else
              :key="`field-${field._uid || index}`"
              class="mb-4"
            >
              <label class="font-weight-bold black--text d-block mb-2">
                {{ field.fieldTitle }}
                <span v-if="field.mandatory" class="red--text">*</span>
              </label>
              <!-- Radio Button Field -->
              <v-radio-group
                v-if="field.dataType === 'radio_button'"
                v-model="formValues[getFieldKey(field, index)]"
                hide-details
                dense
                row
              >
                <v-radio
                  v-for="(option, optIdx) in (field.options || [])"
                  :key="optIdx"
                  :label="option"
                  :value="option"
                ></v-radio>
              </v-radio-group>
              <!-- Checkboxes Field -->
              <div v-else-if="field.dataType === 'check_boxes'">
                <v-checkbox
                  v-for="(option, optIdx) in (field.options || [])"
                  :key="optIdx"
                  v-model="formValues[getFieldKey(field, index)]"
                  :label="option"
                  :value="option"
                  hide-details
                  dense
                  class="mt-0"
                ></v-checkbox>
              </div>
               <!-- Text Field -->
               <v-text-field
                 v-else-if="field.dataType === 'text_field'"
                 v-model="formValues[getFieldKey(field, index)]"
                 :placeholder="getPlaceholder(field.dataType)"
                 outlined
                 dense
                 hide-details
               ></v-text-field>
               <!-- Text Area -->
               <v-textarea
                 v-else-if="field.dataType === 'text_area'"
                 v-model="formValues[getFieldKey(field, index)]"
                 :placeholder="getPlaceholder(field.dataType)"
                 outlined
                 dense
                 hide-details
                 rows="3"
               ></v-textarea>
               <!-- Text Field Numeric -->
               <v-text-field
                 v-else-if="field.dataType === 'text_field_numeric'"
                 v-model="formValues[getFieldKey(field, index)]"
                 :placeholder="getPlaceholder(field.dataType)"
                 type="number"
                 outlined
                 dense
                 hide-details
               ></v-text-field>
               <!-- Dropdown / Data List -->
               <v-select
                 v-else-if="field.dataType === 'dropdown' || field.dataType === 'data_list'"
                 v-model="formValues[getFieldKey(field, index)]"
                 :items="field.options || []"
                 :placeholder="$t('Make a selection')"
                 append-icon="mdi-menu-down"
                 outlined
                 dense
                 hide-details
               ></v-select>
               <!-- Date -->
               <v-text-field
                 v-else-if="field.dataType === 'date_field'"
                 v-model="formValues[getFieldKey(field, index)]"
                 :placeholder="$t('Select date')"
                 type="date"
                 outlined
                 dense
                 hide-details
               ></v-text-field>
                <!-- Attach File -->
                <v-file-input
                   v-else-if="field.dataType === 'attach_file'"
                   v-model="formValues[getFieldKey(field, index)]"
                   :placeholder="$t('Choose file')"
                   prepend-icon=""
                   append-icon="mdi-paperclip"
                   outlined
                   dense
                   hide-details
                ></v-file-input>
                <!-- Measurement Unit -->
                <v-text-field
                   v-else-if="field.dataType === 'measurement_unit'"
                   v-model="formValues[getFieldKey(field, index)]"
                   :placeholder="$t('Enter value')"
                   :suffix="field.measurementType || ''"
                   type="number"
                   outlined
                   dense
                   hide-details
                ></v-text-field>
                <!-- Digital Signature -->
                <DigitalSignatureField
                  v-else-if="field.dataType === 'digital_signature'"
                  :field="signatureFieldStates[getFieldKey(field, index)]"
                />
               <!-- Default fallback -->
               <v-text-field
                 v-else
                 v-model="formValues[getFieldKey(field, index)]"
                 :placeholder="$t('Enter here')"
                 outlined
                 dense
                 hide-details
               ></v-text-field>

              <!-- Help text -->
              <div v-if="field.help && field.helpText" class="grey--text text-caption mt-1">
                {{ field.helpText }}
              </div>
            </div>
          </template>
        </v-form>
        </v-card-text>
      </div>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import DigitalSignatureField from './DigitalSignatureField.vue';

export default {
  name: 'PreviewForm',
  components: {
    DigitalSignatureField,
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    dataFields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      expandedPanels: {}, // Track expanded panels for each group
      formValues: {}, // Local state for preview inputs
      signatureFieldStates: {},
    }
  },
  computed: {
    drawer: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    dataFields: {
      immediate: true,
      handler(fields) {
        // Initialize all group panels as expanded and form values
        this.expandedPanels = {};
        this.formValues = {};
        this.signatureFieldStates = {};
        
        if (fields && fields.length) {
          fields.forEach((field, index) => {
            if (field.isGroup) {
              this.$set(this.expandedPanels, index, [0]);
              // Initialize nested fields
              if (field.groupFields) {
                field.groupFields.forEach((subField, subIndex) => {
                   const key = this.getFieldKey(field, index, subIndex);
                   this.$set(this.formValues, key, this.getInitialValue(subField));
                   this.initializeSignatureFieldState(subField, key);
                });
              }
            } else {
               const key = this.getFieldKey(field, index);
               this.$set(this.formValues, key, this.getInitialValue(field));
               this.initializeSignatureFieldState(field, key);
            }
          });
        }
      }
    }
  },
  methods: {
    close() {
      this.drawer = false;
    },
    getFieldKey(field, index, subIndex = null) {
      if (subIndex !== null) {
        return `${field._uid || index}_sub_${subIndex}`;
      }
      return field._uid || `field_${index}`;
    },
    getInitialValue(field) {
       if (field.dataType === 'check_boxes') return [];
       return null;
    },
    initializeSignatureFieldState(field, key) {
      if (!field || field.dataType !== 'digital_signature') return;
      this.$set(this.signatureFieldStates, key, {
        ...field,
        value: field.value ?? '',
      });
    },
    getPlaceholder(dataType) {
      const placeholders = {
        text_field: this.$t('Enter here'),
        text_area: this.$t('Enter here'),
        text_field_numeric: this.$t('Enter number'),
        dropdown: this.$t('Make a selection'),
        date_field: this.$t('Select date'),
        attach_file: this.$t('Choose file'),
        measurement_unit: this.$t('Enter value')
      };
      return placeholders[dataType] || this.$t('Enter here');
    }
  }
}
</script>

<style scoped>
.preview-drawer {
  z-index: 1000;
}

.preview-content {
  background-color: #f5f5f5;
}

::v-deep .preview-content .v-input__slot {
  background-color: transparent !important;
}

::v-deep .v-expansion-panel {
  background-color: transparent !important;
}

::v-deep .v-expansion-panel::before {
  box-shadow: none !important;
}
::v-deep .v-expansion-panel:not(:first-child)::after {
  border-top: none;
}
::v-deep .v-expansion-panel-header {
  padding-left: 0;
  padding-right: 0;
}
::v-deep .v-expansion-panel-content__wrap {
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
}

::v-deep .v-input--radio-group .v-radio {
  margin-right: 16px;
}
::v-deep .v-window__container {
  height: 100%;
}

::v-deep .v-window-item {
  height: 100%;
  overflow-y: auto;
}

</style>